import React, { useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { TimeDivider } from "./BlockRangeSelector";
 
const getBlockTimeRange = (blockNumber: number): string => {
  const blockNum = blockNumber;
 
  // Each block is 15 minutes, starting from 00:00
  const startMinutes = (blockNum - 1) * 15;
  const endMinutes = blockNum * 15;
 
  // Convert minutes to hours and minutes
  const startHour = Math.floor(startMinutes / 60);
  const startMin = startMinutes % 60;
  const endHour = Math.floor(endMinutes / 60);
  const endMin = endMinutes % 60;
 
  // Convert to 12-hour format
  const formatTo12Hour = (hour: number, minute: number): string => {
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };
 
  const startTime = formatTo12Hour(startHour, startMin);
  const endTime = formatTo12Hour(endHour, endMin);
 
  return `${startTime} - ${endTime}`;
};
 
const BlockRangeUtils = {
  // Get minimum and maximum from start and end blocks
  getMinMaxBlocks: (startBlock: number | null, endBlock: number | null) => {
    if (startBlock === null || endBlock === null)
      return { min: null, max: null };
    return {
      min: Math.min(startBlock, endBlock),
      max: Math.max(startBlock, endBlock),
    };
  },
 
  // Check if block is in range (exclusive of start and end)
  isBlockBetween: (
    block: number,
    startBlock: number | null,
    endBlock: number | null
  ): boolean => {
    if (startBlock === null || endBlock === null) return false;
    const { min, max } = BlockRangeUtils.getMinMaxBlocks(startBlock, endBlock);
    return min !== null && max !== null && block > min && block < max;
  },
 
  // Check if block is in range (inclusive of start and end)
  isBlockInRange: (
    block: number,
    startBlock: number | null,
    endBlock: number | null
  ): boolean => {
    if (startBlock === null || endBlock === null) return false;
    const { min, max } = BlockRangeUtils.getMinMaxBlocks(startBlock, endBlock);
    return min !== null && max !== null && block >= min && block <= max;
  },
 
  // Get all blocks in range (inclusive)
  getAllBlocksInRange: (
    startBlock: number | null,
    endBlock: number | null
  ): number[] => {
    if (startBlock === null || endBlock === null) return [];
    const { min, max } = BlockRangeUtils.getMinMaxBlocks(startBlock, endBlock);
    if (min === null || max === null) return [];
 
    return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  },
 
  // Get blocks count in range
  getBlocksCount: (
    startBlock: number | null,
    endBlock: number | null
  ): number => {
    if (startBlock === null || endBlock === null) return 0;
    return Math.abs(endBlock - startBlock) + 1;
  },
};
 
const BlocksSelector = ({
  blocks,
  setSelectedOptions,
  setOpen,
  selectedOptions,
  setFinalRange,
  finalRange,
}: {
  blocks: number[];
  setSelectedOptions: (selected: number[]) => void;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedOptions: {};
  setFinalRange: React.Dispatch<React.SetStateAction<{}>>;
  finalRange: {};
}) => {
  const BlockSuggestion = useSelector(
    (state: RootState) => state.BlockSuggestion
  );
  const [selectedRange, setSelectedRange] = React.useState<{
    [key: string]: number[];
  }>(finalRange);
 
  const [rangeStarted, setRangeStarted] = React.useState(false);
  const [rangeBlocks, setRangeBlocks] = React.useState<{
    startBlock: number | null;
    endBlock: number | null;
  }>({
    startBlock: null,
    endBlock: null,
  });

  // Handle single block selection
  const handleSingleBlockSelect = useCallback((block: number) => {
    const singleBlockKey = `${block}-${block}`;
    
    setSelectedRange(prevRange => {
      const updatedRange = { ...prevRange };
      
      // Check if this single block already exists
      if (updatedRange[singleBlockKey]) {
        // Remove it (deselect)
        delete updatedRange[singleBlockKey];
        return updatedRange;
      }

      // Check if this block is part of any existing range
      const overlappingKeys: string[] = [];
      Object.keys(prevRange).forEach((key) => {
        const existingBlocks = prevRange[key];
        if (existingBlocks.includes(block)) {
          overlappingKeys.push(key);
        }
      });

      // Remove overlapping ranges and handle fragmentation
      overlappingKeys.forEach((key) => {
        const existingBlocks = prevRange[key];
        const existingMin = Math.min(...existingBlocks);
        const existingMax = Math.max(...existingBlocks);

        // Remove the overlapping range
        delete updatedRange[key];

        // Create new ranges for the remaining blocks
        const beforeBlock: number[] = [];
        const afterBlock: number[] = [];

        // Find blocks before the selected block
        for (let i = existingMin; i < block; i++) {
          if (existingBlocks.includes(i)) {
            beforeBlock.push(i);
          }
        }

        // Find blocks after the selected block
        for (let i = block + 1; i <= existingMax; i++) {
          if (existingBlocks.includes(i)) {
            afterBlock.push(i);
          }
        }

        // Create new ranges for remaining blocks
        if (beforeBlock.length > 0) {
          const beforeMin = Math.min(...beforeBlock);
          const beforeMax = Math.max(...beforeBlock);
          const beforeKey = `${beforeMin}-${beforeMax}`;
          updatedRange[beforeKey] = beforeBlock;
        }

        if (afterBlock.length > 0) {
          const afterMin = Math.min(...afterBlock);
          const afterMax = Math.max(...afterBlock);
          const afterKey = `${afterMin}-${afterMax}`;
          updatedRange[afterKey] = afterBlock;
        }
      });

      // Add the single block selection
      updatedRange[singleBlockKey] = [block];
      return updatedRange;
    });
  }, []);

  const handleRangeClick = useCallback(
    (rangeBlocks: { startBlock: number | null; endBlock: number | null }) => {
      const blocksInRange = BlockRangeUtils.getAllBlocksInRange(
        rangeBlocks.startBlock,
        rangeBlocks.endBlock
      );
 
      function setMini() {
        let mini = Infinity;
        for (let i = 0; i < blocksInRange.length; i++) {
          if (blocksInRange[i] < mini) {
            mini = blocksInRange[i];
          }
        }
        return mini;
      }
 
      function setMaxi() {
        let maxi = -Infinity;
        for (let i = 0; i < blocksInRange.length; i++) {
          if (blocksInRange[i] > maxi) {
            maxi = blocksInRange[i];
          }
        }
        return maxi;
      }
 
      const newRangeMin = setMini();
      const newRangeMax = setMaxi();
      const newKey = `${newRangeMin}-${newRangeMax}`;
 
      // Check if the range already exists
      if (selectedRange[newKey]) {
        return;
      }
 
      // Handle overlapping ranges
      const handleOverlappingRanges = () => {
        const updatedRanges = { ...selectedRange };
        const overlappingKeys: string[] = [];
 
        // Find all overlapping ranges
        Object.keys(selectedRange).forEach((key) => {
          const existingBlocks = selectedRange[key];
          const hasOverlap = blocksInRange.some((block) =>
            existingBlocks.includes(block)
          );
 
          if (hasOverlap) {
            overlappingKeys.push(key);
          }
        });
 
        // If no overlaps, just add the new range
        if (overlappingKeys.length === 0) {
          updatedRanges[newKey] = blocksInRange;
          return updatedRanges;
        }
 
        // Process each overlapping range
        overlappingKeys.forEach((key) => {
          const existingBlocks = selectedRange[key];
          const existingMin = Math.min(...existingBlocks);
          const existingMax = Math.max(...existingBlocks);
 
          // Remove the overlapping range
          delete updatedRanges[key];
 
          // Check for partial overlap and create new ranges
          const beforeOverlap: number[] = [];
          const afterOverlap: number[] = [];
 
          // Find blocks before the new range
          for (let block = existingMin; block < newRangeMin; block++) {
            if (existingBlocks.includes(block)) {
              beforeOverlap.push(block);
            }
          }
 
          // Find blocks after the new range
          for (let block = newRangeMax + 1; block <= existingMax; block++) {
            if (existingBlocks.includes(block)) {
              afterOverlap.push(block);
            }
          }
 
          // Create new ranges if there are remaining blocks
          if (beforeOverlap.length > 0) {
            const beforeMin = Math.min(...beforeOverlap);
            const beforeMax = Math.max(...beforeOverlap);
            const beforeKey = `${beforeMin}-${beforeMax}`;
            updatedRanges[beforeKey] = beforeOverlap;
          }
 
          if (afterOverlap.length > 0) {
            const afterMin = Math.min(...afterOverlap);
            const afterMax = Math.max(...afterOverlap);
            const afterKey = `${afterMin}-${afterMax}`;
            updatedRanges[afterKey] = afterOverlap;
          }
        });
 
        // Add the new range
        updatedRanges[newKey] = blocksInRange;
 
        return updatedRanges;
      };
 
      setSelectedRange(handleOverlappingRanges());
    },
    [selectedRange]
  );
 
  useEffect(() => {
    if (
      BlockSuggestion.startBlock !== null &&
      BlockSuggestion.endBlock !== null &&
      BlockSuggestion.isClicked
    ) {
      handleRangeClick({
        startBlock: BlockSuggestion.startBlock,
        endBlock: BlockSuggestion.endBlock,
      });
    }
    if (
      BlockSuggestion.startBlock !== null &&
      BlockSuggestion.endBlock !== null
    ) {
      const { startBlock, endBlock } = BlockSuggestion;
      setRangeBlocks({ startBlock, endBlock });
      setRangeStarted(true);
    } else {
      setRangeBlocks({ startBlock: null, endBlock: null });
      setRangeStarted(false);
    }
  }, [BlockSuggestion, handleRangeClick]);
 
  useEffect(() => {
    if (
      rangeBlocks.startBlock !== null &&
      rangeBlocks.endBlock !== null &&
      !rangeStarted
    ) {
      handleRangeClick(rangeBlocks);
    }
  }, [handleRangeClick, rangeBlocks, rangeStarted, selectedRange]);
 
  useEffect(() => {
    const handleEsc = (event: { key: string }) => {
      if (event.key === "Escape" && rangeStarted) {
        setRangeBlocks({ startBlock: null, endBlock: null });
        setRangeStarted(false);
      }
    };
 
    document.addEventListener("keydown", handleEsc);
 
    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, [rangeStarted]);
 
  function handleDiselectRange(block: number) {
    const selectedRangeCopy = { ...selectedRange };
    const selectedRangeKeys = Object.keys(selectedRange);
 
    for (let i = 0; i < selectedRangeKeys.length; i++) {
      const range = selectedRangeKeys[i].split("-");
      if (Number(range[0]) <= block && block <= Number(range[1])) {
        delete selectedRangeCopy[selectedRangeKeys[i]];
        setSelectedRange(selectedRangeCopy);
        setRangeBlocks({ startBlock: null, endBlock: null });
        setRangeStarted(false);
        return;
      }
    }
  }
 
  function handleApply() {
    const selectedBlocks: any[] = [];
    for (const key in selectedRange) {
      const range = key.split("-");
      const start = Number(range[0]);
      const end = Number(range[1]);
      for (let block = start; block <= end; block++) {
        selectedBlocks.push({ value: block, label: block });
      }
    }
    setFinalRange(selectedRange);
    setSelectedOptions(selectedBlocks);
    setOpen(false);
  }
 
  return (
    <>
      <div
        onMouseLeave={() => {
          if (!rangeStarted) {
            return;
          }
          setRangeBlocks({ startBlock: null, endBlock: null });
          setRangeStarted(false);
        }}
        className="grid grid-cols-3 gap-2 p-5"
      >
        {TimeDivider.map((time, index) => {
          return (
            <div
              key={index}
              className={`text-xs col-span-3 p-2 rounded-lg ${
                index % 2 === 1 ? "bg-gray-50" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-medium text-gray-700">
                  <span>{time.name}</span>
                  <span> - {time.range}</span>
                </div>
              </div>
 
              <div className="flex flex-wrap gap-1 mt-2">
                {blocks.map((block, index) => {
                  if (time.startBlock <= block && block <= time.endBlock) {
                    return (
                      <BlocksSelectorBtn
                        key={index}
                        index={index}
                        block={block}
                        selectedRange={selectedRange}
                        rangeStarted={rangeStarted}
                        setRangeStarted={setRangeStarted}
                        rangeBlocks={rangeBlocks}
                        setRangeBlocks={setRangeBlocks}
                        handleDiselectRange={handleDiselectRange}
                        handleSingleBlockSelect={handleSingleBlockSelect}
                      />
                    );
                  } else {
                    return null;
                  }
                })}
              </div>
            </div>
          );
        })}
      </div>
 
      <div className="border-t  p-4 px-5  w-full">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => {
                setSelectedRange({});
                setRangeBlocks({ startBlock: null, endBlock: null });
                setRangeStarted(false);
              }}
              className="hover:text-red-500 font-medium text-[0.8rem]  px-5 py-2 rounded-lg hover:border-red-500 transition-colors ease-in-out border duration-100"
            >
              Clear All
            </button>
          </div>
 
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setOpen(false);
                // setSelectedOptions([]);
              }}
              className=" px-5 py-2 rounded-lg border"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                handleApply();
              }}
              className="bg-primary text-white px-5 py-2 rounded-lg hover:bg-primary/90 transition-colors ease-in-out duration-100"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
 
function BlocksSelectorBtn({
  index,
  block,
  rangeStarted,
  setRangeStarted,
  rangeBlocks,
  setRangeBlocks,
  selectedRange,
  handleDiselectRange,
  handleSingleBlockSelect,
}: {
  index: number;
  block: number;
  rangeStarted: boolean;
  setRangeStarted: (value: boolean) => void;
  selectedRange: { [key: string]: number[] };
  rangeBlocks: { startBlock: number | null; endBlock: number | null };
  setRangeBlocks: any;
  handleDiselectRange: (block: number) => void;
  handleSingleBlockSelect: (block: number) => void;
}) {
  const [btnIndex, setBtnIndex] = React.useState<number | null>(null);
  const [clickTimeout, setClickTimeout] = React.useState<NodeJS.Timeout | null>(null);

  // Helper function to check selected range status
  const getSelectedRangeStatus = (block: number) => {
    for (const rangeKey of Object.keys(selectedRange)) {
      const rangeBlocks = selectedRange[rangeKey];
      if (rangeBlocks.includes(block)) {
        const minBlock = Math.min(...rangeBlocks);
        const maxBlock = Math.max(...rangeBlocks);
        const isFirstOrLast = block === minBlock || block === maxBlock;
        const isSingleBlock = minBlock === maxBlock;
        return { inSelectedRange: true, isFirstOrLast, isSingleBlock };
      }
    }
    return { inSelectedRange: false, isFirstOrLast: false, isSingleBlock: false };
  };

  const handleClick = (e: React.MouseEvent) => {
    const { inSelectedRange } = getSelectedRangeStatus(block);
    
    // Check if Ctrl/Cmd key is pressed for single selection
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      handleSingleBlockSelect(block);
      return;
    }

    // If block is already selected, deselect it
    if (!rangeStarted && inSelectedRange) {
      handleDiselectRange(block);
      return;
    }

    // If no range is started
    if (!rangeStarted) {
      // If block is not selected, select it as a single block
      if (!inSelectedRange) {
        handleSingleBlockSelect(block);
        return;
      }
    } else {
      // Complete the range selection
      setRangeBlocks({
        startBlock: rangeBlocks.startBlock,
        endBlock: block,
      });
      setRangeStarted(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const { inSelectedRange } = getSelectedRangeStatus(block);
    
    // Only start range selection on mouse down if we're not using modifier keys
    if (!e.ctrlKey && !e.metaKey && !inSelectedRange && !rangeStarted) {
      // Start range selection mode, but don't select the block yet
      setRangeStarted(true);
      setRangeBlocks({
        startBlock: block,
        endBlock: null,
      });
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Reset range selection
    setRangeStarted(false);
    setRangeBlocks({ startBlock: null, endBlock: null });
    
    // Select single block
    handleSingleBlockSelect(block);
  };
 
  return (
    <div key={index} className="relative">
      <button
        onMouseOver={() => {
          if (rangeStarted) {
            setRangeBlocks({
              startBlock: rangeBlocks.startBlock,
              endBlock: block,
            });
          }
        }}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onMouseEnter={() => setBtnIndex(index)}
        onMouseLeave={() => setBtnIndex(null)}
        className={`h-8 w-8 text-xs rounded-lg transition-colors ease-in-out duration-100 ${(() => {
          const { startBlock, endBlock } = rangeBlocks;

          const { inSelectedRange, isFirstOrLast, isSingleBlock } =
            getSelectedRangeStatus(block);
 
          // Priority 1: Selected ranges (completed selections)
          if (inSelectedRange) {
            if (isSingleBlock) {
              return "bg-primary text-white";
            }
            return isFirstOrLast
              ? "bg-primary text-white"
              : "bg-primary/10 text-primary";
          }
 
          // Priority 2: Current selection endpoints
          if (startBlock === block || endBlock === block) {
            return "bg-primary text-white";
          }
 
          // Priority 3: Current selection preview
          if (BlockRangeUtils.isBlockInRange(block, startBlock, endBlock)) {
            return "bg-primary/10 text-primary";
          }
 
          // Priority 4: Default state
          return "text-[#76797c] hover:border-primary hover:border";
        })()}`}
        title="Click to select single block, drag to select range, Ctrl+Click for toggle"
      >
        {block}
      </button>
      {btnIndex === index && <Tooltip content={getBlockTimeRange(block)} />}
    </div>
  );
}
 
export function Tooltip({ content }: { content: string }) {
  return (
    <div className="absolute top-[-45px] text-nowrap left-1/2 transform transition-all ease-linear duration-150 -translate-x-1/2 z-[99999] inline-block px-3 py-2 font-medium text-xs border text-primary bg-white rounded-lg shadow-lg">
      {content}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-gray-100"></div>
    </div>
  );
}
 
export default BlocksSelector;